/**
 * Worker integration for CarveReliefHandler
 * Handles heavy geometry operations using worker pool
 */
import * as THREE from 'three';
import { geometryWorkerPool } from '../../../utils/WorkerPool.js';
import { DEFORMATION_MODE } from '../../../utils/geometryOperations.js';

/**
 * Compute vertex deformation using worker pool
 * This replaces the expensive getVertexDeformationData function
 */
export async function computeVertexDeformationAsync(
  shadedMesh,
  zone,
  deformationMode = DEFORMATION_MODE.GRADIENT,
  easingFunction = 'linear',
  falloffRadius = null,
  signal = null
) {
  if (!zone.points || zone.points.length < 2) {
    return { vertices: new Uint32Array(0), weights: new Float32Array(0) };
  }

  if (!shadedMesh) {
    return { vertices: new Uint32Array(0), weights: new Float32Array(0) };
  }

  // Check if operation already cancelled before starting
  if (signal?.aborted) {
    throw new DOMException('Operation cancelled', 'AbortError');
  }

  try {
    // Extract geometry data for worker
    const geometry = shadedMesh.geometry;
    const positions = new Float32Array(geometry.attributes.position.array);
    const indices = new Uint32Array(geometry.index.array);
    const normals = new Float32Array(geometry.attributes.normal.array); // **NEW: Extract vertex normals for back-face rejection**
    const matrixArray = new Float32Array(shadedMesh.matrixWorld.elements);

    // Import curve generation utilities
    const { generateCurvePoints, projectCurvePointsOntoMesh, getSurfaceNormalAtPoint } =
      await import('../trimline/trimlineHelpers.js');

    // Generate high-resolution curve for accurate boundary detection
    const baseCurvePositions = generateCurvePoints(
      zone.points,
      zone.bezierHandles,
      400 // High resolution for accuracy
    );

    // Project onto mesh and calculate curve normals
    let curvePoints;
    let curveNormals = [];
    let curveSurfaceNormals = []; // **NEW: Store surface normals for back-face rejection**

    const bvh = geometry.boundsTree;
    if (bvh) {
      curvePoints = projectCurvePointsOntoMesh(baseCurvePositions, shadedMesh, bvh);

      // Calculate curve normals
      const resolution = curvePoints.length / 3;
      for (let i = 0; i < curvePoints.length; i += 3) {
        const point = {
          x: curvePoints[i],
          y: curvePoints[i + 1],
          z: curvePoints[i + 2]
        };

        const surfaceNormal = getSurfaceNormalAtPoint(point, shadedMesh, bvh);
        const t = (i / 3) / (resolution - 1);

        // **Store surface normal for back-face rejection (convert to plain object for serialization)**
        curveSurfaceNormals.push({ x: surfaceNormal.x, y: surfaceNormal.y, z: surfaceNormal.z });

        // Calculate curve tangent inline
        const curveTangent = calculateCurveTangentAtPoint(t, zone.points, zone.bezierHandles);

        // Calculate curve normal as cross product
        const curveNormal = new THREE.Vector3().crossVectors(curveTangent, surfaceNormal).normalize();

        // Convert Vector3 to plain object for worker serialization
        curveNormals.push({ x: curveNormal.x, y: curveNormal.y, z: curveNormal.z });
      }
    } else {
      curvePoints = baseCurvePositions;
    }

    // Execute in worker (spread arguments, not wrap in array)
    // Pass abort signal as last argument for WorkerPool to handle cancellation
    const result = await geometryWorkerPool.execute(
      'computeCarveReliefDeformation',
      positions,
      indices,
      matrixArray,
      curvePoints,
      curveNormals,
      curveSurfaceNormals, // Surface normals at curve points for back-face rejection
      {
        deformationMode,
        easingFunction,
        falloffRadius
      },
      normals, // **NEW: Vertex normals for back-face rejection**
      signal // Pass abort signal to worker pool
    );

    return result;
  } catch (error) {
    if (error.name === 'AbortError') {
      console.log('[CarveReliefWorkerIntegration] Deformation computation cancelled');
      return null;
    }
    console.error('[CarveReliefWorkerIntegration] Error computing deformation:', error);
    throw error;
  }
}

/**
 * Apply vertex displacement using worker pool
 */
export async function applyVertexDisplacementAsync(
  baseGeometry,
  affectedVertices,
  weights,
  delta,
  signal = null
) {
  try {
    const basePositions = new Float32Array(baseGeometry.attributes.position.array);
    const baseNormals = new Float32Array(baseGeometry.attributes.normal.array);

    const result = await geometryWorkerPool.execute(
      'applyVertexDisplacement',
      basePositions,
      baseNormals,
      affectedVertices,
      weights,
      delta
    );

    return result;
  } catch (error) {
    if (error.name === 'AbortError') {
      console.log('[CarveReliefWorkerIntegration] Displacement application cancelled');
      return null;
    }
    console.error('[CarveReliefWorkerIntegration] Error applying displacement:', error);
    throw error;
  }
}

/**
 * Apply geometric smoothing using worker pool
 */
export async function smoothVerticesAsync(
  geometry,
  vertexIndices,
  iterations = 1,
  strength = 0.5,
  signal = null
) {
  try {
    const positions = new Float32Array(geometry.attributes.position.array);
    const indices = new Uint32Array(geometry.index.array);
    const vertexIndicesArray = new Uint32Array(vertexIndices);

    const result = await geometryWorkerPool.execute(
      'smoothVertices',
      positions,
      indices,
      vertexIndicesArray,
      iterations,
      strength
    );

    return result;
  } catch (error) {
    if (error.name === 'AbortError') {
      console.log('[CarveReliefWorkerIntegration] Vertex smoothing cancelled');
      return null;
    }
    console.error('[CarveReliefWorkerIntegration] Error smoothing vertices:', error);
    throw error;
  }
}

/**
 * Calculate bezier curve tangent at parameter t
 */
function calculateBezierTangent(t, p0, p1, p2, p3) {
  const u = 1 - t;
  const tangent = new THREE.Vector3();

  tangent.addScaledVector(p0, -3 * u * u);
  tangent.addScaledVector(p1, 3 * u * u - 6 * u * t);
  tangent.addScaledVector(p2, 6 * u * t - 3 * t * t);
  tangent.addScaledVector(p3, 3 * t * t);

  return tangent.normalize();
}

/**
 * Calculate curve tangent at a specific point along the curve
 */
export function calculateCurveTangentAtPoint(t, points, bezierHandles) {
  if (!points || points.length < 2) {
    return new THREE.Vector3(1, 0, 0);
  }

  const numSegments = points.length;
  const segmentIndex = Math.floor(t * numSegments);
  const segmentT = (t * numSegments) - segmentIndex;

  const currentIndex = segmentIndex % numSegments;
  const nextIndex = (segmentIndex + 1) % numSegments;

  const p0 = new THREE.Vector3(points[currentIndex].x, points[currentIndex].y, points[currentIndex].z);
  const p3 = new THREE.Vector3(points[nextIndex].x, points[nextIndex].y, points[nextIndex].z);

  const handles = bezierHandles[currentIndex];
  if (!handles) {
    return p3.clone().sub(p0).normalize();
  }

  const p1 = new THREE.Vector3(
    p0.x + handles.out.x,
    p0.y + handles.out.y,
    p0.z + handles.out.z
  );

  const nextHandles = bezierHandles[nextIndex];
  const p2 = new THREE.Vector3(
    p3.x + (nextHandles ? nextHandles.in.x : 0),
    p3.y + (nextHandles ? nextHandles.in.y : 0),
    p3.z + (nextHandles ? nextHandles.in.z : 0)
  );

  return calculateBezierTangent(segmentT, p0, p1, p2, p3);
}
